import { Modal, Descriptions, Row, Popover, Button, Col, Select } from 'antd';
import { ThreeDotsVertical, Eye, Power } from 'react-bootstrap-icons';
import { useEffect, useState, useCallback } from 'react';
import { useNotificationContext } from '../../contexts/NotificationContext';
import { useTranslation } from 'react-i18next';
import ConfirmModelComponent from '../model/ConfirmationModelComponent';
import { alertConfigService } from '../../api/services/alertConfigService';
import TableComponent from '../common/TableComponent';
import { ActionMenu } from '../common/ActionMenu';
import dayjs from 'dayjs';
import { referenceDataSyncService } from '../../services/referenceDataSyncService';
import { getPriceConfigList } from './getData';
import { getTranslatedMetricOptions } from '../../enums/PriceAlertConfigTriggeringPriceEnum';
import Tag from '../tag/Tag';
import { normalize } from '../../utils/normalizeWords';
import { PermissionEnum } from '../../enums/PermissionEnum';
import { usePermissions } from '../../hooks/usePermissions';

interface PriceAlertConfigListProps {
  listUpdated?: number;
}

interface PriceAlertConfig {
  id: number;
  basketIds?: number[];
  productRefId?: string;
  productCategoryIds?: number[];
  triggeringCondition: string;
  fromDateRange?: number;
  toDateRange?: number;
  startMonitoringDateRange?: number;
  endMonitoringDateRange?: number;
  price?: number;
  avgPrice?: number;
  condition?: string;
  threshold?: number;
  numberOfInstance: number;
  locationId?: number;
  enableAlert: boolean;
  isActive: boolean;
  alertCount?: number;
  userId?: string;
  createTime?: number | string;
  baskets?: Array<{
    refId: string;
    name: string;
  }>;
  product?: {
    refId: string;
    name: string;
  };
  productCategories?: Array<{
    id: number;
    mainCategory: string;
    category: string;
    subCategory?: string;
  }>;
  location?: {
    id: number;
    district: string;
    city: string;
  };
}

type PriceAlertConfigMenuItem = {
  text: string;
  icon: React.ReactNode;
  click: () => void;
};

const PriceAlertConfigList: React.FC<PriceAlertConfigListProps> = ({ listUpdated }) => {
  const { t } = useTranslation('priceAlertsConfiguration');
  const { successNotification, errorNotification } = useNotificationContext();
  const { hasPermission } = usePermissions();
  const canViewAlertConfigs = hasPermission(PermissionEnum.VIEW_CONFIG_ALERTS);
  const canConfigureAlerts = hasPermission(PermissionEnum.CONFIG_ALERTS);
  const canShowAlertConfigActions = canViewAlertConfigs || canConfigureAlerts;

  const [priceAlertConfigData, setPriceAlertConfigData] = useState<PriceAlertConfig[]>([]);
  const [isLoadingPriceAlertConfig, setIsLoadingPriceAlertConfig] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPriceAlertConfigs, setTotalPriceAlertConfigs] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [isConfirmClicked, setIsConfirmClicked] = useState<boolean>(false);

  const [isToggleConfirmationOpen, setIsToggleConfirmationOpen] = useState<boolean>(false);
  const [selectedAlertId, setSelectedAlertId] = useState<number>();
  const [checkedVal, setCheckedVal] = useState<boolean>(true);
  const [isViewModalOpen, setIsViewModalOpen] = useState<boolean>(false);
  const [selectedAlertConfig, setSelectedAlertConfig] = useState<PriceAlertConfig | null>(null);

  const [isLoadingFilterData, setIsLoadingFilterData] = useState<boolean>(false);
  const [presentations, setPresentations] = useState<any[]>([]);
  const [departments, setDepartments] = useState<string[]>([]);
  const [municipalities, setMunicipalities] = useState<string[]>([]);
  const [locations, setLocations] = useState<Array<{ district: string; city: string; id: number }>>(
    []
  );

  const [selectedPresentations, setSelectedPresentations] = useState<string[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<string | undefined>(undefined);
  const [selectedMunicipality, setSelectedMunicipality] = useState<string | undefined>(undefined);
  const [selectedMetric, setSelectedMetric] = useState();
  const [selectedLocationIds, setSelectedLocationIds] = useState<number[]>([]);

  const [enableClearFiltersBtn, setEnableClearFiltersBtn] = useState<boolean>(false);

  useEffect(() => {
    if (canViewAlertConfigs || canConfigureAlerts) {
      getAllPriceAlertConfigList();
      fetchLocationsData();
    }
  }, [canConfigureAlerts, canViewAlertConfigs]);

  const fetchLocationsData = useCallback(async () => {
    try {
      const locationsData = await referenceDataSyncService.getLocations();
      const formattedLocations = locationsData.map((location: Record<string, unknown>) => ({
        district: location.district as string,
        city: location.city as string,
        id: location.id as number,
      }));
      setLocations(formattedLocations);

      const uniqueDepartments = Array.from(
        new Set(formattedLocations.map((location) => location.district))
      );
      setDepartments(uniqueDepartments);
    } catch (error) {
      console.error('Failed to load locations from IndexedDB:', error);
    }
  }, []);

  useEffect(() => {
    if (selectedDepartment) {
      const locationIdsForSelectedDepartment = locations
        .filter((location) => location.district === selectedDepartment)
        .map((location) => location.id);

      setSelectedLocationIds(locationIdsForSelectedDepartment);
      const filteredMunicipalities = locations
        .filter((location) => location.district === selectedDepartment)
        .map((location) => location.city);
      setMunicipalities(filteredMunicipalities);
      setSelectedMunicipality(undefined);
    } else {
      setMunicipalities([]);
      setSelectedMunicipality(undefined);
      setSelectedLocationIds([]);
    }
  }, [selectedDepartment, locations]);

  useEffect(() => {
    if (selectedMunicipality && selectedDepartment) {
      const locationIdsForSelectedMunicipality = locations
        .filter(
          (location) =>
            location.district === selectedDepartment && location.city === selectedMunicipality
        )
        .map((location) => location.id);

      setSelectedLocationIds(locationIdsForSelectedMunicipality);
    } else if (selectedMunicipality) {
      const locationIdsForSelectedMunicipality = locations
        .filter((location) => location.city === selectedMunicipality)
        .map((location) => location.id);

      setSelectedLocationIds(locationIdsForSelectedMunicipality);
    } else if (selectedDepartment) {
      const locationIdsForSelectedDepartment = locations
        .filter((location) => location.district === selectedDepartment)
        .map((location) => location.id);

      setSelectedLocationIds(locationIdsForSelectedDepartment);
    } else {
      setSelectedLocationIds([]);
    }
  }, [selectedMunicipality]);

  const fetchFilterData = useCallback(async () => {
    if (!canViewAlertConfigs && !canConfigureAlerts) {
      return;
    }

    setIsLoadingFilterData(true);
    try {
      const presentationsData = await referenceDataSyncService.getPresentationsForFilters();
      setPresentations(presentationsData);
    } catch (error) {
      console.error('Error fetching filter data:', error);
    } finally {
      setIsLoadingFilterData(false);
    }
  }, [canConfigureAlerts, canViewAlertConfigs, errorNotification]);

  useEffect(() => {
    if (canViewAlertConfigs || canConfigureAlerts) {
      getAllPriceAlertConfigList(1);
      fetchFilterData();
    }
  }, [canConfigureAlerts, canViewAlertConfigs, fetchFilterData]);

  const getAllPriceAlertConfigList = useCallback(
    async (page: number = currentPage, filters?: any, customPageSize?: number) => {
      if (!canViewAlertConfigs && !canConfigureAlerts) {
        return;
      }

      setIsLoadingPriceAlertConfig(true);
      try {
        const effectivePageSize = customPageSize !== undefined ? customPageSize : pageSize;

        const paginationParams = {
          page,
          limit: effectivePageSize,
        };

        const filterParams: any = {
          ...paginationParams,
        };

        if (
          filters?.presentations &&
          Array.isArray(filters?.presentations) &&
          filters?.presentations.length > 0
        ) {
          filterParams.productRefIds = filters?.presentations;
        }

        if (filters?.triggeringCondition) {
          filterParams.triggeringCondition = filters?.triggeringCondition;
        }

        if (
          filters?.locationIds &&
          Array.isArray(filters?.locationIds) &&
          filters?.locationIds.length > 0
        ) {
          filterParams.locationIds = filters?.locationIds;
        }

        const response = await getPriceConfigList(filterParams);

        if (response.statusText === 'SUCCESS') {
          const priceAlertConfigs = response.data?.data || response.data || [];
          const total = response.data?.total || 0;

          const locations = await referenceDataSyncService.getLocations();
          const locationMap = new Map(locations.map((loc) => [loc.id, loc]));

          const enrichedConfigs = priceAlertConfigs.map((config: PriceAlertConfig) => ({
            ...config,
            location: config.locationId ? locationMap.get(config.locationId) : null,
          }));

          setPriceAlertConfigData(enrichedConfigs);

          setTotalPriceAlertConfigs(total);
          setCurrentPage(page);
        } else {
        }
      } catch (error) {
        console.error('Failed to fetch presentations from backend:', error);
      } finally {
        setIsLoadingPriceAlertConfig(false);
      }
    },
    [canConfigureAlerts, canViewAlertConfigs, pageSize, errorNotification, t]
  );

  useEffect(() => {
    const filters = {
      products: selectedPresentations,
      locationIds: selectedLocationIds,
      triggeringCondition: selectedMetric,
    };

    const hasFilters =
      selectedPresentations.length > 0 ||
      selectedMetric !== undefined ||
      selectedLocationIds.length > 0;

    if (!canViewAlertConfigs && !canConfigureAlerts) {
      return;
    }

    if (hasFilters) {
      getAllPriceAlertConfigList(1, filters);
      setEnableClearFiltersBtn(true);
    } else {
      getAllPriceAlertConfigList(1, {});
      setEnableClearFiltersBtn(false);
    }
  }, [
    canConfigureAlerts,
    canViewAlertConfigs,
    selectedPresentations,
    selectedMetric,
    selectedLocationIds,
    getAllPriceAlertConfigList,
  ]);

  const clearAllFilters = () => {
    setSelectedPresentations([]);
    setSelectedDepartment(undefined);
    setSelectedMunicipality(undefined);
    setSelectedMetric(undefined);
    setSelectedLocationIds([]);

    getAllPriceAlertConfigList(1, {});
  };

  const handleToggleAlertStatus = () => {
    if (selectedAlertId === undefined) {
      return;
    }

    updateToggleAlertStatus(selectedAlertId, checkedVal);
  };

  const updateToggleAlertStatus = async (id: number, checked: boolean) => {
    if (!canConfigureAlerts) {
      return;
    }

    setPriceAlertConfigData((prevConfig) =>
      prevConfig.map((config) => {
        return config.id === id ? { ...config, enableAlert: checked } : config;
      })
    );
    // To disable the confirm button and close the confirmation popup to avoid multiple clicks
    setIsConfirmClicked(true);
    setIsToggleConfirmationOpen(false);

    try {
      const result = await alertConfigService.updateAlertConfig(id, { enableAlert: checked });

      if (result) {
        successNotification(t('priceAlertUpdateSuccess'));
        // setIsToggleConfirmationOpen(false);
      }
    } catch {
      // setIsToggleConfirmationOpen(false);
      errorNotification(t('priceAlertUpdateFail'));
    } finally {
      setIsConfirmClicked(false);
    }
  };

  useEffect(() => {
    getAllPriceAlertConfigList();
  }, [listUpdated, getAllPriceAlertConfigList]);

  const cancelToggleConfirmation = () => {
    setIsToggleConfirmationOpen(false);
  };

  const handleViewAlertConfig = (record: PriceAlertConfig) => {
    setSelectedAlertConfig(record);
    setIsViewModalOpen(true);
  };

  const closeViewModal = () => {
    setIsViewModalOpen(false);
    setSelectedAlertConfig(null);
  };

  const getToggleConfirmation = (id: number, checked: boolean) => {
    if (!canConfigureAlerts) {
      return;
    }

    setSelectedAlertId(id);
    setCheckedVal(checked);
    setIsToggleConfirmationOpen(true);
  };

  const onPageChange = (page: number, size?: number) => {
    if (!canViewAlertConfigs && !canConfigureAlerts) {
      return;
    }

    const newPageSize = size && size !== pageSize ? size : pageSize;

    setCurrentPage(page);
    if (size && size !== pageSize) {
      setPageSize(size);
    }

    const filters = {
      products: selectedPresentations,
      locationIds: selectedLocationIds,
      triggeringCondition: selectedMetric,
    };

    // Call getAllPriceAlertConfigList with updated page size
    getAllPriceAlertConfigList(page, filters, newPageSize);
  };

  const isDatePassed = (record: any) => {
    const isPastDay = record?.endMonitoringDateRange
      ? dayjs(Number(record?.endMonitoringDateRange)).isBefore(dayjs(), 'day')
      : dayjs(Number(record)).isBefore(dayjs(), 'day');

    return isPastDay;
  };

  const getAlertConfigColumns = () => [
    {
      title: t('product'),
      dataIndex: 'product',
      key: 'product',
      width: 200,
      render: (product: { name?: string }) => (
        <span className="font-medium">{product?.name || 'N/A'}</span>
      ),
    },
    {
      title: t('alertCount'),
      dataIndex: 'alertCount',
      key: 'alertCount',
      width: 120,
      align: 'center' as const,
      render: (alertCount: number) => (
        <span className="font-medium text-gray-600">{alertCount || 0}</span>
      ),
    },
    {
      title: t('department'),
      dataIndex: 'location',
      key: 'department',
      width: 150,
      render: (location: { district?: string }) => (
        <span className="text-gray-600">{location?.district || t('all')}</span>
      ),
    },
    {
      title: t('municipality'),
      dataIndex: 'location',
      key: 'municipality',
      width: 150,
      render: (location: { city?: string }) => (
        <span className="text-gray-600">{location?.city || t('all')}</span>
      ),
    },
    {
      title: t('triggeringCondition'),
      dataIndex: 'triggeringCondition',
      key: 'triggeringCondition',
      width: 150,
      render: (condition: string) => (
        <span className="text-gray-600">
          {condition === 'manual_price' ? t('manualPrice') : t('averagePrice')}
        </span>
      ),
    },
    {
      title: t('alertDetails'),
      key: 'alertDetails',
      width: 200,
      render: (record: PriceAlertConfig) => {
        if (record.triggeringCondition === 'manual_price') {
          const conditionText =
            record.condition === 'equal'
              ? t('equal')
              : record.condition === 'less_than'
                ? t('lessThan')
                : record.condition === 'greater_than'
                  ? t('greaterThan')
                  : record.condition || 'N/A';

          return (
            <span className="text-sm text-gray-600">
              {t('alertWhenInstance', {
                count: record.numberOfInstance,
                price: record.price ? Math.round(record.price).toLocaleString() : 'N/A',
                condition: conditionText,
              })}
            </span>
          );
        } else {
          const conditionText =
            record.condition === 'above_threshold'
              ? 'above'
              : record.condition === 'below_threshold'
                ? 'below'
                : 'above or below';

          let displayPrice = 'Price not set';

          if (record.avgPrice && record.threshold) {
            const avgPrice = parseFloat(record?.avgPrice);
            const threshold = parseFloat(record?.threshold);

            if (!isNaN(avgPrice) && !isNaN(threshold)) {
              if (record?.condition === 'above_threshold') {
                const upperThreshold = avgPrice + (avgPrice * threshold) / 100;
                displayPrice = ` ${Math.round(upperThreshold).toLocaleString()}`;
              } else if (record?.condition === 'below_threshold') {
                const lowerThreshold = avgPrice - (avgPrice * threshold) / 100;
                displayPrice = ` ${Math.round(lowerThreshold).toLocaleString()}`;
              } else {
                const upperThreshold = avgPrice + (avgPrice * threshold) / 100;
                const lowerThreshold = avgPrice - (avgPrice * threshold) / 100;
                displayPrice = ` ${Math.round(upperThreshold).toLocaleString()} or L. ${Math.round(lowerThreshold).toLocaleString()}`;
              }
            } else {
              displayPrice = 'Invalid price data';
            }
          } else if (record?.avgPrice) {
            const avgPrice = parseFloat(record?.avgPrice);
            displayPrice = !isNaN(avgPrice)
              ? `L. ${Math.round(avgPrice).toLocaleString()}`
              : 'Invalid price';
          } else if (record?.threshold) {
            displayPrice = `${record?.threshold}% threshold`;
          }

          return (
            <span className="text-sm text-gray-600">
              {t('alertWhenPrice', {
                count: record?.numberOfInstance,
                condition: conditionText,
                avgPrice: displayPrice,
              })}
            </span>
          );
        }
      },
    },
    {
      title: t('status'),
      dataIndex: 'enableAlert',
      key: 'status',
      width: 100,
      render: (enableAlert: boolean, record: string) => (
        <Tag tagType={enableAlert ? (isDatePassed(record) ? 'neutral' : 'success') : 'neutral'}>
          {enableAlert ? (isDatePassed(record) ? t('inactive') : t('active')) : t('inactive')}
        </Tag>
      ),
    },
    {
      title: t('created'),
      dataIndex: 'createTime',
      key: 'createTime',
      width: 120,
      align: 'center' as const,
      render: (createTime: string) => (
        <span className="text-gray-600">
          {dayjs(new Date(Number(createTime))).format('YYYY-MM-DD')}
        </span>
      ),
    },
    ...(canShowAlertConfigActions
      ? [
          {
            title: t('action'),
            dataIndex: 'action',
            key: 'action',
            width: 100,
            align: 'center' as const,
            render: (_: unknown, record: PriceAlertConfig) => {
              const menuItems: PriceAlertConfigMenuItem[] = [];

              if (canViewAlertConfigs) {
                menuItems.push({
                  text: t('view'),
                  icon: <Eye size={16} className="text-primary-blue-icon" />,
                  click: () => handleViewAlertConfig(record),
                });
              }

              if (canConfigureAlerts && !isDatePassed(record?.endMonitoringDateRange)) {
                menuItems.push({
                  text: record.enableAlert ? t('disable') : t('enable'),
                  icon: <Power size={16} className="text-primary-blue-icon" />,
                  click: () => getToggleConfirmation(record.id, !record.enableAlert),
                });
              }

              if (menuItems.length === 0) {
                return null;
              }

              return (
                <Popover
                  content={<ActionMenu menuItems={menuItems} />}
                  className="cursor-pointer"
                  placement="bottom"
                  trigger="hover"
                  overlayStyle={{ zIndex: 800 }}
                >
                  <Row justify="center">
                    <ThreeDotsVertical size={15} />
                  </Row>
                </Popover>
              );
            },
          },
        ]
      : []),
  ];

  if (!canViewAlertConfigs && !canConfigureAlerts) {
    return null;
  }

  return (
    <>
      {/* filter section */}
      <div className="my-6">
        {/* <BasicCard> */}
        {/* <div className="p-4"> */}
        <section className="mb-8 w-full">
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">{t('filters')}</h3>
              <Button
                type="link"
                onClick={clearAllFilters}
                className="hover:text-primary-dark text-sm text-primary"
                disabled={!enableClearFiltersBtn}
              >
                {t('clearFilters')}
              </Button>
            </div>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8} lg={5}>
                <div className="mb-2">
                  <label className="text-sm font-medium text-gray-700">{t('presentations')}</label>
                </div>
                <Select
                  className="w-full [&>.ant-select-selector]:!rounded-lg"
                  placeholder={t('presentationsFilterPlaceholder')}
                  mode="multiple"
                  maxTagCount={1}
                  maxTagTextLength={window.matchMedia('(min-width: 1600px)').matches ? 15 : 10}
                  allowClear
                  onClear={() => setSelectedPresentations([])}
                  value={selectedPresentations}
                  onChange={setSelectedPresentations}
                  loading={isLoadingFilterData}
                  options={presentations.map((product: any) => ({
                    label: product.name,
                    value: product.refId,
                  }))}
                  showSearch
                  filterOption={(input, option) =>
                    normalize(option?.label ?? '')
                      .toString()
                      .toLowerCase()
                      .includes(normalize(input.toLowerCase()))
                  }
                />
              </Col>

              <Col xs={24} sm={12} md={8} lg={4}>
                <div className="mb-2">
                  <label className="text-sm font-medium text-gray-700">{t('metricLabel')}</label>
                </div>
                <Select
                  className="w-full [&>.ant-select-selector]:!rounded-lg"
                  placeholder={t('metricFieldPlaceholder')}
                  allowClear
                  onClear={() => setSelectedMetric(undefined)}
                  value={selectedMetric}
                  onChange={setSelectedMetric}
                  loading={isLoadingFilterData}
                  options={getTranslatedMetricOptions(t)}
                  showSearch
                  filterOption={(input, option) =>
                    normalize(option?.label ?? '')
                      .toString()
                      .toLowerCase()
                      .includes(normalize(input.toLowerCase()))
                  }
                />
              </Col>

              <Col xs={24} sm={12} md={8} lg={4}>
                <div className="mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    {t('departmentLabel')}
                  </label>
                </div>
                <Select
                  className="w-full [&>.ant-select-selector]:!rounded-lg"
                  placeholder={t('departmentFieldPlaceholder')}
                  allowClear
                  onClear={() => setSelectedDepartment(undefined)}
                  value={selectedDepartment || undefined}
                  onChange={setSelectedDepartment}
                  options={departments.map((dept) => ({
                    label: dept,
                    value: dept,
                  }))}
                  loading={isLoadingFilterData}
                  showSearch
                  filterOption={(input, option) =>
                    normalize(option?.label ?? '')
                      .toString()
                      .toLowerCase()
                      .includes(normalize(input.toLowerCase()))
                  }
                />
              </Col>

              <Col xs={24} sm={12} md={8} lg={4}>
                <div className="mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    {t('municipalityLabel')}
                  </label>
                </div>
                <Select
                  className="w-full [&>.ant-select-selector]:!rounded-lg"
                  placeholder={t('municipalityFieldPlaceholder')}
                  allowClear
                  onClear={() => setSelectedMunicipality(undefined)}
                  value={selectedMunicipality || undefined}
                  onChange={setSelectedMunicipality}
                  disabled={!selectedDepartment || municipalities.length === 0}
                  options={municipalities.map((municipality) => ({
                    label: municipality,
                    value: municipality,
                  }))}
                  loading={isLoadingFilterData}
                  showSearch
                  filterOption={(input, option) =>
                    normalize(option?.label ?? '')
                      .toString()
                      .toLowerCase()
                      .includes(normalize(input.toLowerCase()))
                  }
                />
              </Col>
            </Row>
          </div>
        </section>

        {/* <BasicCard> */}
        {/* <div className="p-4"> */}
        <div className="mt-6 w-full overflow-x-auto">
          <TableComponent
            tableData={priceAlertConfigData}
            pageSize={pageSize}
            currentPage={currentPage}
            totalItems={totalPriceAlertConfigs}
            onPaginationChanged={onPageChange}
            columns={getAlertConfigColumns()}
            loading={isLoadingPriceAlertConfig}
            showHeader
          />
        </div>
        {/* </div> */}
        {/* </BasicCard> */}
        {/* </div> */}
        {/* </BasicCard> */}
      </div>

      <ConfirmModelComponent
        isOpen={isToggleConfirmationOpen}
        onClickedCancel={cancelToggleConfirmation}
        header={t('confirmStatusToggleTitle')}
        content={
          <p>
            {t('confirmStatusToggleMessage', {
              status: checkedVal ? t('enable') : t('disable'),
            })}
          </p>
        }
        footerAction={{
          confimation: {
            label: t('confirm'),
            onClick: () => handleToggleAlertStatus(),
            isDisabled: isConfirmClicked,
          },
          cancel: {
            label: t('cancel'),
            onClick: cancelToggleConfirmation,
          },
        }}
        styleClasses="z-100"
      />

      <Modal
        title={t('alertConfigDetails')}
        open={isViewModalOpen}
        onCancel={closeViewModal}
        footer={null}
        width={800}
        centered
      >
        {selectedAlertConfig && (
          <Descriptions column={1} bordered>
            <Descriptions.Item label={t('triggeringCondition')}>
              <Tag tagType="neutral">
                {selectedAlertConfig?.triggeringCondition === 'manual_price'
                  ? t('manualPrice')
                  : t('averagePrice')}
              </Tag>
            </Descriptions.Item>

            {selectedAlertConfig?.baskets && selectedAlertConfig?.baskets.length > 0 && (
              <Descriptions.Item label={t('baskets')}>
                {selectedAlertConfig?.baskets.map((basket, index) => (
                  <Tag key={index} tagType="neutral">
                    {basket.name}
                  </Tag>
                ))}
              </Descriptions.Item>
            )}

            {selectedAlertConfig?.product && (
              <Descriptions.Item label={t('product')}>
                <Tag tagType="neutral">{selectedAlertConfig?.product?.name}</Tag>
              </Descriptions.Item>
            )}

            {selectedAlertConfig?.productCategories &&
              selectedAlertConfig?.productCategories?.length > 0 && (
                <Descriptions.Item label={t('productCategories')}>
                  {selectedAlertConfig?.productCategories.map((category, index) => (
                    <Tag key={index} tagType="neutral">
                      {[category?.mainCategory, category?.category].filter(Boolean).join(' - ')}
                    </Tag>
                  ))}
                </Descriptions.Item>
              )}

            {selectedAlertConfig?.location && (
              <Descriptions.Item label={t('location')}>
                {selectedAlertConfig?.location?.district}, {selectedAlertConfig?.location?.city}
              </Descriptions.Item>
            )}

            {selectedAlertConfig?.condition && (
              <Descriptions.Item label={t('condition')}>
                <Tag tagType="neutral">
                  {selectedAlertConfig?.condition === 'equal'
                    ? t('equal')
                    : selectedAlertConfig?.condition === 'less_than'
                      ? t('lessThan')
                      : selectedAlertConfig?.condition === 'greater_than'
                        ? t('greaterThan')
                        : selectedAlertConfig?.condition === 'above_threshold'
                          ? t('aboveThreshold')
                          : selectedAlertConfig?.condition === 'below_threshold'
                            ? t('belowThreshold')
                            : selectedAlertConfig?.condition === 'all'
                              ? t('all')
                              : selectedAlertConfig?.condition || 'N/A'}
                </Tag>
              </Descriptions.Item>
            )}

            {selectedAlertConfig?.price && (
              <Descriptions.Item label={t('manualPrice')}>
                L. {selectedAlertConfig?.price.toLocaleString()}
              </Descriptions.Item>
            )}

            {selectedAlertConfig?.avgPrice && (
              <Descriptions.Item label={t('averagePrice')}>
                L. {selectedAlertConfig?.avgPrice.toLocaleString()}
              </Descriptions.Item>
            )}

            {selectedAlertConfig?.threshold && (
              <Descriptions.Item label={t('threshold')}>
                {selectedAlertConfig?.threshold}%
              </Descriptions.Item>
            )}

            <Descriptions.Item label={t('numberOfInstances')}>
              {selectedAlertConfig?.numberOfInstance}
            </Descriptions.Item>

            <Descriptions.Item label={t('alertCount')}>
              <Tag tagType="neutral">{selectedAlertConfig?.alertCount || 0}</Tag>
            </Descriptions.Item>

            <Descriptions.Item label={t('status')}>
              <Tag
                tagType={
                  selectedAlertConfig?.enableAlert
                    ? isDatePassed(selectedAlertConfig?.endMonitoringDateRange)
                      ? 'neutral'
                      : 'success'
                    : 'neutral'
                }
              >
                {selectedAlertConfig?.enableAlert
                  ? isDatePassed(selectedAlertConfig?.endMonitoringDateRange)
                    ? t('inactive')
                    : t('active')
                  : t('inactive')}
              </Tag>
            </Descriptions.Item>

            {selectedAlertConfig?.fromDateRange && (
              <Descriptions.Item label={t('fromDateRange')}>
                {dayjs(Number(selectedAlertConfig?.fromDateRange)).format('YYYY-MM-DD')}
              </Descriptions.Item>
            )}

            {selectedAlertConfig?.toDateRange && (
              <Descriptions.Item label={t('toDateRange')}>
                {dayjs(Number(selectedAlertConfig?.toDateRange)).format('YYYY-MM-DD')}
              </Descriptions.Item>
            )}

            {selectedAlertConfig?.startMonitoringDateRange && (
              <Descriptions.Item label={t('startMonitoringDateRange')}>
                {dayjs(Number(selectedAlertConfig?.startMonitoringDateRange)).format('YYYY-MM-DD')}
              </Descriptions.Item>
            )}

            {selectedAlertConfig?.endMonitoringDateRange && (
              <Descriptions.Item label={t('endMonitoringDateRange')}>
                {dayjs(Number(selectedAlertConfig?.endMonitoringDateRange)).format('YYYY-MM-DD')}
              </Descriptions.Item>
            )}

            <Descriptions.Item label={t('createdAt')}>
              {dayjs(Number(selectedAlertConfig?.createTime)).format('YYYY-MM-DD')}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </>
  );
};

export default PriceAlertConfigList;
